"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { logger } from "@/lib/logger";

interface OnboardingContextType {
  showOnboarding: boolean;
  hasCompleted: boolean;
  isLoading: boolean;
  completeOnboarding: () => Promise<void>;
  skipOnboarding: () => Promise<void>;
  restartOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

interface OnboardingProviderProps {
  children: ReactNode;
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const [hasCompleted, setHasCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch onboarding status from API on mount
  const fetchOnboardingStatus = async () => {
    setIsLoading(true);

    try {
      const response = await fetch("/api/user/onboarding");

      if (!response.ok) {
        throw new Error("Failed to fetch onboarding status");
      }

      const data = await response.json();
      setHasCompleted(data.hasCompletedOnboarding);
    } catch (err) {
      logger.error("Failed to fetch onboarding status:", err instanceof Error ? { message: err.message, stack: err.stack } : { error: err });
      // Default to completed on error to avoid showing onboarding if there's an issue
      setHasCompleted(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch onboarding status on mount
  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  // Complete onboarding - marks as fully completed
  const completeOnboarding = async () => {
    try {
      const response = await fetch("/api/user/onboarding", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to complete onboarding");
      }

      const data = await response.json();
      setHasCompleted(data.hasCompletedOnboarding);
      logger.info("Onboarding completed successfully");
    } catch (err) {
      logger.error("Failed to complete onboarding:", err instanceof Error ? { message: err.message, stack: err.stack } : { error: err });
      throw err; // Re-throw to allow UI to handle the error
    }
  };

  // Skip onboarding - marks as completed but via skip
  const skipOnboarding = async () => {
    try {
      const response = await fetch("/api/user/onboarding", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ skip: true }),
      });

      if (!response.ok) {
        throw new Error("Failed to skip onboarding");
      }

      const data = await response.json();
      setHasCompleted(data.hasCompletedOnboarding);
      logger.info("Onboarding skipped");
    } catch (err) {
      logger.error("Failed to skip onboarding:", err instanceof Error ? { message: err.message, stack: err.stack } : { error: err });
      throw err; // Re-throw to allow UI to handle the error
    }
  };

  // Restart onboarding - sets hasCompletedOnboarding to false
  const restartOnboarding = () => {
    // Update local state immediately for better UX
    setHasCompleted(false);

    // Update on the backend
    fetch("/api/user/onboarding", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ hasCompletedOnboarding: false }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to restart onboarding");
        }
        return response.json();
      })
      .then((data) => {
        setHasCompleted(data.hasCompletedOnboarding);
        logger.info("Onboarding restarted");
      })
      .catch((err) => {
        logger.error("Failed to restart onboarding:", err instanceof Error ? { message: err.message, stack: err.stack } : { error: err });
        // Revert local state on error
        setHasCompleted(true);
      });
  };

  const value: OnboardingContextType = {
    showOnboarding: !hasCompleted && !isLoading,
    hasCompleted,
    isLoading,
    completeOnboarding,
    skipOnboarding,
    restartOnboarding,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

/**
 * Hook to use onboarding context
 *
 * @throws {Error} If used outside of OnboardingProvider
 *
 * @example
 * ```tsx
 * const { showOnboarding, completeOnboarding, skipOnboarding } = useOnboarding();
 *
 * if (showOnboarding) {
 *   return <OnboardingWizard onComplete={completeOnboarding} onSkip={skipOnboarding} />;
 * }
 * ```
 */
export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
}
